// src/utils/style-helpers.ts

import type { StyleConfig, Preset } from '../types'
import { PRESETS } from '../constants/defaults'

/**
 * Merge preset style with custom overrides
 */
export function mergeStyle(
  preset: Preset = 'default',
  customStyle?: Partial<StyleConfig>,
  customAccentColor?: string,
  customBorderRadius?: 'sharp' | 'rounded'
): { accentColor: string; style: Required<StyleConfig> } {
  const presetConfig = PRESETS[preset] || PRESETS.default

  return {
    accentColor: customAccentColor || presetConfig.accentColor,
    style: {
      ...presetConfig.style,
      ...customStyle,
      borderRadius:
        customBorderRadius || customStyle?.borderRadius || presetConfig.style.borderRadius,
      border: {
        ...presetConfig.style.border,
        ...customStyle?.border,
      },
      fontSize: {
        ...presetConfig.style.fontSize,
        ...customStyle?.fontSize,
      },
      button: {
        ...presetConfig.style.button,
        ...customStyle?.button,
      },
      modal: {
        ...presetConfig.style.modal,
        ...customStyle?.modal,
      },
      animation: {
        ...presetConfig.style.animation,
        ...customStyle?.animation,
      },
    },
  }
}

/**
 * Get Tailwind position classes
 */
export function getPositionClasses(position?: string): string {
  if (!position) return 'bottom-4 left-4 md:left-6 md:bottom-6'

  const positions: Record<string, string> = {
    'bottom-left': 'bottom-4 left-4 md:left-6 md:bottom-6',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4 md:right-6 md:bottom-6',
    'top-left': 'top-4 left-4 md:left-6 md:top-6',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4 md:right-6 md:top-6',
  }

  return positions[position] || positions['bottom-left']
}

/**
 * Get max width class
 */
export function getMaxWidthClass(maxWidth?: string): string {
  if (!maxWidth) return 'md:max-w-md'

  const sizes: Record<string, string> = {
    sm: 'md:max-w-sm',
    md: 'md:max-w-md',
    lg: 'md:max-w-lg',
    xl: 'md:max-w-xl',
  }

  if (sizes[maxWidth]) {
    return sizes[maxWidth]
  }

  // Custom width - no class needed, use inline style
  return ''
}

/**
 * Get spacing/padding class
 */
export function getSpacingClass(spacing?: string): string {
  if (!spacing) return 'p-4'

  const spacings: Record<string, string> = {
    compact: 'p-3',
    normal: 'p-4',
    comfortable: 'p-6',
  }

  return spacings[spacing] || spacings.normal
}

/**
 * Get border radius class
 */
export function getBorderRadiusClass(radius?: string): string {
  if (!radius) return 'rounded-xl'

  const radii: Record<string, string> = {
    sharp: 'rounded-none',
    rounded: 'rounded-xl',
  }

  return radii[radius] || radii.rounded
}

/**
 * Get border radius value for inline styles
 */
export function getBorderRadiusValue(radius?: string): string {
  if (!radius) return '12px'

  const radii: Record<string, string> = {
    sharp: '0',
    rounded: '12px',
  }

  return radii[radius] || radii.rounded
}

/**
 * Get shadow class
 */
export function getShadowClass(shadow?: string): string {
  if (!shadow) return 'shadow-lg'

  const shadows: Record<string, string> = {
    none: 'shadow-none',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    '2xl': 'shadow-2xl',
  }

  return shadows[shadow] || shadows.lg
}

/**
 * Get border width class
 */
export function getBorderWidthClass(width?: string): string {
  if (!width) return 'border'

  const widths: Record<string, string> = {
    none: 'border-0',
    '1': 'border',
    '2': 'border-2',
  }

  return widths[width] || widths['1']
}

/**
 * Get font size class
 */
export function getFontSizeClass(size?: string, type: 'heading' | 'body' = 'body'): string {
  if (!size) return type === 'heading' ? 'text-sm' : 'text-xs'

  const sizes: Record<string, Record<string, string>> = {
    heading: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
    },
    body: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-base',
    },
  }

  return sizes[type][size] || sizes[type].sm
}

/**
 * Get button shape class
 */
export function getButtonShapeClass(shape?: string): string {
  if (!shape) return 'rounded-full'

  const shapes: Record<string, string> = {
    square: 'rounded-none',
    rounded: 'rounded-lg',
    pill: 'rounded-full',
  }

  return shapes[shape] || shapes.pill
}

/**
 * Get button size class
 */
export function getButtonSizeClass(size?: string): string {
  if (!size) return 'px-4 py-2 text-xs'

  const sizes: Record<string, string> = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-xs',
    lg: 'px-6 py-3 text-sm',
  }

  return sizes[size] || sizes.md
}

/**
 * Get animation values for framer-motion
 */
export function getAnimationValues(direction?: string): {
  initial: Record<string, number>
  animate: Record<string, number>
  exit: Record<string, number>
} {
  if (!direction) direction = 'up'

  const animations: Record<
    string,
    {
      initial: Record<string, number>
      animate: Record<string, number>
      exit: Record<string, number>
    }
  > = {
    up: {
      initial: { y: 100, opacity: 0 },
      animate: { y: 0, opacity: 1 },
      exit: { y: 100, opacity: 0 },
    },
    down: {
      initial: { y: -100, opacity: 0 },
      animate: { y: 0, opacity: 1 },
      exit: { y: -100, opacity: 0 },
    },
    left: {
      initial: { x: 100, opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: 100, opacity: 0 },
    },
    right: {
      initial: { x: -100, opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { x: -100, opacity: 0 },
    },
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
  }

  return animations[direction] || animations.up
}
